import { RESEARCH_DEFAULT_OPTION, defaultNote } from '../../lib/copy';

/**
 * One settings choice: a select whose first option is the default, and — only
 * while that option is the one selected — the line saying what the default is.
 *
 * The default line is **conditional on purpose**. Printed under a control that
 * has been set, "Default: Opus" reads as a description of the current value,
 * which is exactly the misreading it exists to prevent. Once something is
 * chosen, the control says what it is.
 *
 * `help` is optional because the depth controls share one explanation between
 * them: three selects each repeating what Full, Standard and Light mean would be
 * the same paragraph three times.
 *
 * It lives in `components/ui` as a general control; the research settings'
 * depth controls are its caller. `RESEARCH_DEFAULT_OPTION` is the first
 * option's words.
 */
export function ChoiceField<T extends string>({
  id,
  label,
  value,
  options,
  labels,
  defaultLabel,
  help,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  value: T | '';
  options: readonly T[];
  labels: Readonly<Record<string, string>>;
  defaultLabel: string | null;
  help?: string;
  disabled: boolean;
  onChange: (value: T | '') => void;
}) {
  return (
    <div className="field">
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value as T | '')}
      >
        <option value="">{RESEARCH_DEFAULT_OPTION}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {labels[option] ?? option}
          </option>
        ))}
      </select>
      {value === '' && defaultLabel !== null ? (
        <div className="field-note">{defaultNote(defaultLabel)}</div>
      ) : null}
      {help ? <div className="field-note">{help}</div> : null}
    </div>
  );
}
